import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import {
	useAutomationTemplateContactStepsQuery,
	useAutomationTemplateEligibleContactsQuery,
} from '../../../../queries/Automation';
import { useContact } from '../../../../queries/Contact/useContact';
import { useContactSearchQuery } from '../../../../queries/Contact/useContactSearchQuery';
import { Button } from '../../../../web/components-v2/Button';
import { Input, InputWrapper } from '../../../../web/components-v2/Input';
import { SearchIcon2 } from '../../../../web/components/svgs/icons/SearchIcon2';
import { CONTACT_COLORS } from '../../../../web/containers/AutomationFlows/types/contacts';
import { ContactCard } from '../ContactCard';
import { ContactProgressIndicator } from './ContactProgressIndicator';
import { styleSheet } from './styles';
import { type SegmentFilterMode, TestContact } from './types';

interface TestingContactsDrawerContentProps {
	templateId: string;
	selectedContacts: TestContact[];
	onContactsChange: React.Dispatch<React.SetStateAction<TestContact[]>>;
	impersonationContext?: Api.IImpersonationContext;
	useDraft: boolean;
	segmentFilterMode?: SegmentFilterMode;
}

const MAX_CONTACTS = 5;

function areStepListsEquivalent(previousSteps?: TestContact['steps'], nextSteps?: TestContact['steps']): boolean {
	if (previousSteps === nextSteps) {
		return true;
	}

	const previousLength = previousSteps?.length ?? 0;
	const nextLength = nextSteps?.length ?? 0;
	if (previousLength !== nextLength) {
		return false;
	}
	if (previousLength === 0) {
		return true;
	}
	if (!previousSteps || !nextSteps) {
		return false;
	}

	for (let i = 0; i < previousSteps.length; i++) {
		const previous = previousSteps[i];
		const next = nextSteps[i];
		if (
			previous.id !== next.id ||
			previous.step?.id !== next.step?.id ||
			previous.status?.status !== next.status?.status ||
			(previous.status as { outcome?: string } | undefined)?.outcome !==
				(next.status as { outcome?: string } | undefined)?.outcome ||
			previous.dueDate !== next.dueDate ||
			previous.isFinished !== next.isFinished
		) {
			return false;
		}
	}

	return true;
}

export const TestingContactsDrawerContent: React.FC<TestingContactsDrawerContentProps> = ({
	templateId,
	selectedContacts,
	onContactsChange,
	impersonationContext,
	useDraft,
	segmentFilterMode = 'unknown',
}) => {
	const { data: eligibleContactsData, isLoading: isLoadingEligible } = useAutomationTemplateEligibleContactsQuery({
		templateId,
		limit: MAX_CONTACTS,
		impersonationContext,
		useDraft,
	});

	const eligibleContacts = React.useMemo(() => eligibleContactsData?.values ?? [], [eligibleContactsData?.values]);
	const eligibleContactsTotal = eligibleContactsData?.totalCount ?? eligibleContacts.length;
	const maxReached = selectedContacts.length >= MAX_CONTACTS;
	const remainingSlots = Math.max(MAX_CONTACTS - selectedContacts.length, 0);

	const selectedContactIds = React.useMemo(
		() => new Set(selectedContacts.map(tc => tc.contact.id)),
		[selectedContacts]
	);

	const availableEligibleContacts = React.useMemo(
		() =>
			eligibleContacts.filter((contact: Api.IContact) => !selectedContactIds.has(contact.id)).slice(0, remainingSlots),
		[eligibleContacts, selectedContactIds, remainingSlots]
	);

	const [searchInput, setSearchInput] = React.useState('');
	const debouncedSearch = useDebounceValue(searchInput.trim(), 400);
	const shouldSearch = debouncedSearch.length >= 2 && !maxReached;
	const {
		data: contactSearchData,
		isFetching: isSearchingContacts,
		isError: isSearchError,
	} = useContactSearchQuery({
		searchQuery: debouncedSearch,
		pageSize: MAX_CONTACTS,
		enabled: shouldSearch,
		impersonationContext,
	});

	const filteredSearchResults = React.useMemo(() => {
		return (contactSearchData?.values || []).filter((contact: Api.IContact) => !selectedContactIds.has(contact.id));
	}, [contactSearchData?.values, selectedContactIds]);

	const handleAddContact = (contact: Api.IContact) => {
		onContactsChange(previousContacts => {
			const previousIds = new Set(previousContacts.map(tc => tc.contact.id));
			if (previousContacts.length >= MAX_CONTACTS || previousIds.has(contact.id)) {
				return previousContacts;
			}

			const color = CONTACT_COLORS[previousContacts.length % CONTACT_COLORS.length];
			const newTestContact: TestContact = {
				contact,
				color,
				isSelected: true,
				steps: undefined,
			};

			return [...previousContacts, newTestContact];
		});
	};

	const handleToggleSelect = (contactId: string) => {
		onContactsChange(previousContacts => {
			const existingIndex = previousContacts.findIndex(tc => tc.contact.id === contactId);
			if (existingIndex < 0) {
				return previousContacts;
			}

			return previousContacts
				.filter((_, index) => index !== existingIndex)
				.map((tc, index) => ({ ...tc, color: CONTACT_COLORS[index % CONTACT_COLORS.length] }));
		});
	};

	const handleClearAll = () => {
		onContactsChange([]);
	};

	const renderContactOptionRow = (contact: Api.IContact, source: 'search' | 'eligible') => {
		const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unnamed Contact';

		return (
			<li key={`${source}-${contact.id}`} className={css(styleSheet.contactOptionRow)}>
				<div className={css(styleSheet.contactOptionInfo)}>
					<div className={css(styleSheet.contactOptionName)}>{fullName}</div>
					{contact.primaryEmail?.value ? (
						<div className={css(styleSheet.contactOptionEmail)}>{contact.primaryEmail.value}</div>
					) : (
						<div className={css(styleSheet.contactOptionSecondaryText)}>No primary email</div>
					)}
				</div>
				<Button variant='secondary' size='sm' onClick={() => handleAddContact(contact)} disabled={maxReached}>
					Add
				</Button>
			</li>
		);
	};

	const renderSearchResults = () => {
		if (maxReached) {
			return <p className={css(styleSheet.searchHint)}>Remove a selected contact to add another.</p>;
		}

		if (!searchInput.trim()) {
			return <p className={css(styleSheet.searchHint)}>Search by contact name or email address.</p>;
		}

		if (debouncedSearch.length < 2) {
			return <p className={css(styleSheet.searchHint)}>Enter at least two characters to search.</p>;
		}

		if (isSearchingContacts) {
			return <p className={css(styleSheet.emptyState)}>Searching contacts...</p>;
		}

		if (isSearchError) {
			return <p className={css(styleSheet.emptyState)}>Unable to search contacts. Please try again.</p>;
		}

		if (filteredSearchResults.length === 0) {
			return <p className={css(styleSheet.emptyState)}>No contacts found.</p>;
		}

		return (
			<ul className={css(styleSheet.contactOptions)}>
				{filteredSearchResults.map(contact => renderContactOptionRow(contact, 'search'))}
			</ul>
		);
	};

	return (
		<div className={css(styleSheet.drawerContent)}>
			<section className={css(styleSheet.section)}>
				<h3 className={css(styleSheet.sectionHeader)}>Search Contacts</h3>
				<p className={css(styleSheet.sectionDescription)}>
					Find contacts by name or email. Search uses the current impersonated account so testing reflects real data.
				</p>
				<div className={css(styleSheet.searchInputRow)}>
					<InputWrapper styles={[styleSheet.searchInputWrapper]}>
						<SearchIcon2 width={16} height={16} />
						<Input
							type='text'
							id='automation-testing-contact-search'
							value={searchInput}
							onChange={e => setSearchInput(e.target.value)}
							placeholder={maxReached ? 'Contact limit reached' : 'Search by name or email'}
							disabled={maxReached}
						/>
					</InputWrapper>
					{searchInput ? (
						<Button variant='ghost' size='sm' onClick={() => setSearchInput('')}>
							Clear
						</Button>
					) : null}
				</div>
				{renderSearchResults()}
			</section>

			<section className={css(styleSheet.section)}>
				<div className={css(styleSheet.sectionHeaderRow)}>
					<h3 className={css(styleSheet.sectionHeader)}>Eligible Contacts</h3>
					{eligibleContactsTotal > 0 ? (
						<span className={css(styleSheet.sectionMeta)}>{eligibleContactsTotal} total eligible</span>
					) : null}
				</div>
				<p className={css(styleSheet.sectionDescription)}>
					These contacts currently match your trigger criteria and can be added quickly for testing.
				</p>
				{isLoadingEligible ? (
					<div className={css(styleSheet.emptyState)}>Loading eligible contacts...</div>
				) : maxReached ? (
					<div className={css(styleSheet.emptyState)}>
						Contact limit reached. Remove a selected contact to add more.
					</div>
				) : availableEligibleContacts.length === 0 ? (
					<div className={css(styleSheet.emptyState)}>No additional eligible contacts found.</div>
				) : (
					<ul className={css(styleSheet.contactOptions)}>
						{availableEligibleContacts.map(contact => renderContactOptionRow(contact, 'eligible'))}
					</ul>
				)}
			</section>

			<section className={css(styleSheet.section)}>
				<div className={css(styleSheet.selectedContactsHeader)}>
					<div>
						<h3 className={css(styleSheet.sectionHeader, styleSheet.sectionHeaderNoMargin)}>Selected Contacts</h3>
						<p className={css(styleSheet.sectionDescription, styleSheet.sectionDescriptionNoMargin)}>
							{selectedContacts.length}/{MAX_CONTACTS} selected for simulation.
						</p>
					</div>
					{selectedContacts.length > 0 ? (
						<Button variant='destructive-secondary' size='sm' onClick={handleClearAll}>
							Clear All
						</Button>
					) : null}
				</div>

				{selectedContacts.length === 0 ? (
					<div className={css(styleSheet.emptyState)}>No contacts selected yet.</div>
				) : (
					<div className={css(styleSheet.selectedContactsList)}>
						{selectedContacts.map(testContact => (
							<ContactCardWithSteps
								key={testContact.contact.id}
								testContact={testContact}
								templateId={templateId}
								onToggleSelect={handleToggleSelect}
								impersonationContext={impersonationContext}
								useDraft={useDraft}
								onContactsChange={onContactsChange}
								segmentFilterMode={segmentFilterMode}
							/>
						))}
					</div>
				)}

				{maxReached ? (
					<div className={css(styleSheet.contactLimit)}>Maximum of {MAX_CONTACTS} contacts selected.</div>
				) : null}
			</section>
		</div>
	);
};

const ContactCardWithSteps: React.FC<{
	testContact: TestContact;
	templateId: string;
	onToggleSelect: (contactId: string) => void;
	impersonationContext?: Api.IImpersonationContext;
	useDraft: boolean;
	onContactsChange: React.Dispatch<React.SetStateAction<TestContact[]>>;
	segmentFilterMode: SegmentFilterMode;
}> = ({
	testContact,
	templateId,
	onToggleSelect,
	impersonationContext,
	useDraft,
	onContactsChange,
	segmentFilterMode,
}) => {
	const shouldPrioritizePolicySignals = segmentFilterMode === 'policy' || segmentFilterMode === 'mixed';

	const { data: detailedContact } = useContact({
		contactId: testContact.contact.id || '',
		enabled: shouldPrioritizePolicySignals && Boolean(testContact.contact.id),
		refetchOnWindowFocus: false,
	});

	const { data: steps } = useAutomationTemplateContactStepsQuery({
		templateId,
		contactId: testContact.contact.id,
		useDraft,
		impersonationContext,
	});

	// Update parent state when steps are loaded.
	// Use functional updates to avoid stale-closure races when multiple contact
	// queries resolve around the same time.
	React.useEffect(() => {
		if (!steps) {
			return;
		}

		onContactsChange(previousContacts => {
			const existingContact = previousContacts.find(tc => tc.contact.id === testContact.contact.id);
			if (!existingContact || areStepListsEquivalent(existingContact.steps, steps)) {
				return previousContacts;
			}

			return previousContacts.map(tc => (tc.contact.id === testContact.contact.id ? { ...tc, steps } : tc));
		});
	}, [steps, testContact.contact.id, onContactsChange]);

	const contactForCard = React.useMemo(
		() => ({
			...testContact.contact,
			...detailedContact,
			tags: detailedContact?.tags ?? testContact.contact.tags,
			keyFacts: detailedContact?.keyFacts ?? testContact.contact.keyFacts,
			keyFactsCollection: detailedContact?.keyFactsCollection ?? testContact.contact.keyFactsCollection,
		}),
		[detailedContact, testContact.contact]
	);

	const contactWithSteps: TestContact = {
		...testContact,
		contact: contactForCard,
		steps: steps || testContact.steps,
	};

	const showAllTags = segmentFilterMode === 'tag';

	return (
		<ContactCard
			contact={contactWithSteps.contact}
			onToggle={onToggleSelect}
			isSelected={contactWithSteps.isSelected}
			color={contactWithSteps.color}
			showEmail={true}
			showPhone={true}
			showAllTags={showAllTags}
			showAllKeyFacts={shouldPrioritizePolicySignals}
			renderAdditionalContent={() => {
				if (contactWithSteps.isSelected && contactWithSteps.steps && contactWithSteps.steps.length > 0) {
					return <ContactProgressIndicator steps={contactWithSteps.steps} />;
				}
				return null;
			}}
		/>
	);
};
